import {
  Component, ElementRef, EventEmitter, HostBinding, HostListener, Input, Output, ViewChild, forwardRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AVAILABLE_UI_LANGUAGES, UiLanguage } from '../../core/services/language.service';

/**
 * Sélecteur de langue custom (popover) — remplace le `<select>` natif dont la liste
 * déroulante est rendue par l'OS et ne peut pas être stylée. Utilisable soit en
 * ControlValueAccessor (`formControlName`), soit en binding direct ([value]/(valueChange)).
 */
@Component({
  selector: 'app-language-select',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './language-select.component.html',
  styleUrls: ['./language-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => LanguageSelectComponent),
    multi: true,
  }],
})
export class LanguageSelectComponent implements ControlValueAccessor {
  /** 'field' (modales/formulaires, pleine largeur) ou 'compact' (barre login, largeur au contenu). */
  @Input() variant: 'field' | 'compact' = 'field';
  @Input() ariaLabel = 'Langue';
  @Output() valueChange = new EventEmitter<string>();

  readonly languages: UiLanguage[] = AVAILABLE_UI_LANGUAGES;
  selected = 'fr';
  open = false;
  disabled = false;

  @HostBinding('class.field') get isField(): boolean { return this.variant === 'field'; }
  @HostBinding('class.compact') get isCompact(): boolean { return this.variant === 'compact'; }

  @ViewChild('trigger') private triggerRef?: ElementRef<HTMLElement>;

  @Input()
  set value(v: string | null | undefined) {
    if (v) this.selected = v;
  }

  get selectedLabel(): string {
    return this.languages.find(l => l.code === this.selected)?.label ?? this.selected;
  }

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(v: string): void {
    if (v) this.selected = v;
  }
  registerOnChange(fn: (value: string) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }

  toggle(): void {
    if (this.disabled) return;
    this.open = !this.open;
  }

  select(code: string): void {
    this.selected = code;
    this.open = false;
    this.onChange(code);
    this.onTouched();
    this.valueChange.emit(code);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.open && !this.triggerRef?.nativeElement.parentElement?.contains(event.target as Node)) {
      this.open = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.open = false;
  }
}
